import { useTranslation } from 'react-i18next';

interface FeatureCard {
  icon: string;
  titleKey: string;
  descKey: string;
  imageUrl: string;
  imageAlt: string;
  wide?: boolean;
}

export default function FeaturesSection() {
  const { t } = useTranslation();

  const features: FeatureCard[] = [
    {
      icon: 'ri-settings-3-line',
      titleKey: 'features.production',
      descKey: 'features.production_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20textile%20production%20floor%20with%20digital%20screens%20displaying%20real-time%20production%20metrics%2C%20weaving%20machines%20in%20background%2C%20clean%20industrial%20aesthetic%2C%20warm%20ambient%20lighting%2C%20minimalist%20factory%20interior%2C%20professional%20manufacturing%20environment%2C%20high%20detail%20architectural%20photography&width=800&height=560&seq=feat-production&orientation=landscape',
      imageAlt: 'Production management interface',
      wide: true,
    },
    {
      icon: 'ri-store-2-line',
      titleKey: 'features.warehouse',
      descKey: 'features.warehouse_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Organized%20textile%20warehouse%20with%20labeled%20fabric%20rolls%20on%20industrial%20shelving%2C%20digital%20inventory%20tablet%20in%20foreground%2C%20clean%20bright%20space%2C%20warm%20neutral%20tones%2C%20minimalist%20logistics%20aesthetic%2C%20professional%20product%20photography&width=560&height=560&seq=feat-warehouse&orientation=squarish',
      imageAlt: 'Warehouse and inventory',
    },
    {
      icon: 'ri-truck-line',
      titleKey: 'features.supply_chain',
      descKey: 'features.supply_chain_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Supply%20chain%20logistics%20control%20center%20with%20digital%20maps%20and%20tracking%20screens%2C%20shipping%20containers%20visualization%2C%20warm%20amber%20accents%2C%20clean%20modern%20office%20environment%2C%20professional%20corporate%20aesthetic%2C%20minimalist%20design&width=560&height=560&seq=feat-supply&orientation=squarish',
      imageAlt: 'Supply chain management',
    },
    {
      icon: 'ri-bank-line',
      titleKey: 'features.finance',
      descKey: 'features.finance_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20financial%20dashboard%20displaying%20charts%20and%20accounting%20data%20on%20clean%20computer%20screen%2C%20warm%20office%20environment%20with%20natural%20light%2C%20minimalist%20desk%20setup%2C%20professional%20corporate%20aesthetic%2C%20calculator%20and%20documents%20nearby&width=560&height=400&seq=feat-finance&orientation=landscape',
      imageAlt: 'Finance and accounting',
    },
    {
      icon: 'ri-team-line',
      titleKey: 'features.hr',
      descKey: 'features.hr_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Human%20resources%20management%20interface%20on%20laptop%20screen%20showing%20employee%20scheduling%20and%20attendance%20data%2C%20warm%20modern%20office%20setting%2C%20clean%20desk%20with%20plant%2C%20natural%20lighting%2C%20professional%20environment&width=560&height=400&seq=feat-hr&orientation=landscape',
      imageAlt: 'Human resources',
    },
    {
      icon: 'ri-shield-check-line',
      titleKey: 'features.quality',
      descKey: 'features.quality_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Quality%20control%20inspection%20station%20in%20textile%20factory%20with%20fabric%20samples%20and%20digital%20testing%20equipment%2C%20clean%20white%20surface%2C%20warm%20professional%20lighting%2C%20minimalist%20laboratory%20aesthetic%2C%20fabric%20swatches%20neatly%20arranged&width=560&height=400&seq=feat-quality&orientation=landscape',
      imageAlt: 'Quality control',
    },
    {
      icon: 'ri-bar-chart-2-line',
      titleKey: 'features.analytics',
      descKey: 'features.analytics_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Business%20analytics%20dashboard%20showing%20KPI%20charts%20and%20production%20efficiency%20data%20on%20large%20monitor%2C%20warm%20modern%20office%20with%20natural%20light%2C%20minimalist%20workspace%2C%20professional%20tech%20environment&width=560&height=400&seq=feat-analytics&orientation=landscape',
      imageAlt: 'Analytics and dashboards',
    },
  ];

  const topRow = features.slice(0, 3);
  const bottomRow = features.slice(3);

  return (
    <section id="features" className="bg-background-100">
      <div className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 md:gap-6 mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight text-foreground-950 shrink-0">
            {t('features.title')}
            <br />
            {t('features.title_line2')}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-md lg:text-right leading-relaxed">
            {t('features.subtitle')}
          </p>
        </div>

        {/* Top Row — 3 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-4 md:mb-5">
          {topRow.map((feat) => (
            <div
              key={feat.titleKey}
              className={`group bg-white rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 ${
                feat.wide ? 'md:col-span-1' : ''
              }`}
            >
              <div className="relative h-48 md:h-56 overflow-hidden">
                <img
                  src={feat.imageUrl}
                  alt={feat.imageAlt}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
              </div>
              <div className={`p-4 md:p-5 ${feat.wide ? 'md:flex md:flex-col' : ''}`}>
                <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2">
                  {t(feat.titleKey)}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  {t(feat.descKey)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Row — 4 Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {bottomRow.map((feat) => (
            <div
              key={feat.titleKey}
              className="group bg-white rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300"
            >
              <div className="relative h-36 md:h-44 overflow-hidden">
                <img
                  src={feat.imageUrl}
                  alt={feat.imageAlt}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent"></div>
              </div>
              <div className="p-3.5 md:p-5">
                <h3 className="text-sm md:text-base font-bold text-foreground-950 mb-1.5">
                  {t(feat.titleKey)}
                </h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  {t(feat.descKey)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* View All */}
        <div className="mt-10 text-center">
          <a
            href="#ai-features"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors duration-200 cursor-pointer"
          >
            {t('features.view_all')}
            <i className="ri-arrow-down-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}